import { useCallback, useMemo } from "react"
import { Box, MenuItem } from "@mui/material"
import type { MRT_ColumnDef, MRT_Row, MRT_RowSelectionState } from "material-react-table"
import { makeStyles } from "tss-react/mui"

import { useApiQueryTable } from "../../../../../../core/api/components/ApiQueryTable"
import { isOrderRowControlClick } from "../../../../../../shared/components/meetingServiceOrderDrawer/orderDrawerRow"
import { DestructiveMenuItem } from "../../../../../../shared/components/menus/DestructiveMenuItem"
import { GROW_CELL_CLASS, useGridLayoutStyles } from "../../../../../../shared/components/table/useGridLayoutStyles"
import { COLUMN_SIZE, defaultDisplayColumnDefOptions } from "../../../../../../shared/constants/table/config"
import { LocalizedStrict } from "../../../../../localization/components/AppLocalized"
import { useAppLocalization } from "../../../../../localization/components/AppLocalizationProvider"
import { UserNameCell } from "../../users/components/UserNameCell"

import type { WorkgroupMemberRow } from "../workgroupsTypes"
import { SelectDepartmentDropdown } from "./SelectDepartmentDropdown"

const useStyles = makeStyles()(() => ({
    toolbar: {
        display: "flex",
        flexWrap: "wrap",
        alignItems: "center",
        gap: "1rem",
        padding: "1rem",
    },
    table: {
        ".MuiTableCell-root": {
            paddingLeft: "1rem",
            paddingRight: "1rem",
        },
        ".MuiTableBody-root .MuiTableCell-root": {
            overflowWrap: "anywhere",
        },
        ".Mui-TableHeadCell-Content-Labels": {
            paddingLeft: 0,
        },
    },
    actionsCell: {
        paddingLeft: "0 !important",
        paddingRight: "0 !important",
        overflow: "visible",
    },
    row: {
        cursor: "pointer",
    },
    inactiveRow: {
        opacity: 0.6,
    },
}))

type RowActionMenuItemsArgs = Readonly<{
    row: MRT_Row<WorkgroupMemberRow>
    closeMenu: () => void
}>

type WorkgroupMembersTableProps = Readonly<{
    members: WorkgroupMemberRow[]
    departments: ReadonlyArray<string>
    department: string
    onDepartmentChange: (department: string) => void
    isLoading: boolean
    isFetching: boolean
    isError: boolean
    rowSelection: MRT_RowSelectionState
    onRowSelectionChange: (rowSelection: MRT_RowSelectionState) => void
    onRemove: (member: WorkgroupMemberRow) => void
    onShowDetails: (member: WorkgroupMemberRow) => void
}>

export function WorkgroupMembersTable({
    members,
    departments,
    department,
    onDepartmentChange,
    isLoading,
    isFetching,
    isError,
    rowSelection,
    onRowSelectionChange,
    onRemove,
    onShowDetails,
}: WorkgroupMembersTableProps) {
    const { classes, cx } = useStyles()
    const { classes: gridClasses } = useGridLayoutStyles()
    const { l10n } = useAppLocalization()

    const { ApiQueryTable, columnFactory } = useApiQueryTable({
        data: members,
        isInitialLoading: isLoading,
        isFetching,
        isError,
    })

    const columns = useMemo<MRT_ColumnDef<WorkgroupMemberRow>[]>(
        () => [
            {
                header: l10n.getString("create-or-edit-company-workgroup-members-table-header-user-id"),
                accessorKey: "id",
                size: COLUMN_SIZE.S,
                minSize: COLUMN_SIZE.S,
                maxSize: COLUMN_SIZE.S,
                enableColumnActions: false,
            },
            columnFactory.nameColumn({
                header: l10n.getString("create-or-edit-company-workgroup-members-table-header-name"),
                accessorKey: "displayName",
                size: COLUMN_SIZE.S,
                minSize: COLUMN_SIZE.S,
                maxSize: COLUMN_SIZE.XXXL,
                Cell: ({ row }) => <UserNameCell user={row.original} />,
                muiTableHeadCellProps: { className: GROW_CELL_CLASS },
                muiTableBodyCellProps: { className: GROW_CELL_CLASS },
            }),
            {
                header: l10n.getString("create-or-edit-company-workgroup-members-table-header-email"),
                accessorFn: (member) => member.email ?? "",
                id: "email",
                size: COLUMN_SIZE.S,
                minSize: COLUMN_SIZE.S,
                maxSize: COLUMN_SIZE.XXXL,
                muiTableHeadCellProps: { className: GROW_CELL_CLASS },
                muiTableBodyCellProps: { className: GROW_CELL_CLASS },
            },
            {
                ...columnFactory.activeInactiveStatusColumn(
                    {
                        header: l10n.getString("create-or-edit-company-workgroup-members-table-header-status"),
                        accessorKey: "isActive",
                    },
                    (member) => member.isActive,
                ),
                size: COLUMN_SIZE.S,
                minSize: COLUMN_SIZE.S,
                maxSize: COLUMN_SIZE.S,
            },
        ],
        [l10n, columnFactory],
    )

    const renderRowActionMenuItems = useCallback(
        ({ row, closeMenu }: RowActionMenuItemsArgs) => [
            <MenuItem
                key={`details-${row.original.id}`}
                onClick={() => {
                    onShowDetails(row.original)
                    closeMenu()
                }}
            >
                <LocalizedStrict id="create-or-edit-company-workgroup-members-details-button">
                    <span>Details</span>
                </LocalizedStrict>
            </MenuItem>,
            <DestructiveMenuItem
                key={`remove-${row.original.id}`}
                onClick={() => {
                    onRemove(row.original)
                    closeMenu()
                }}
            >
                <LocalizedStrict id="create-or-edit-company-workgroup-members-remove">
                    <span>Remove from group</span>
                </LocalizedStrict>
            </DestructiveMenuItem>,
        ],
        [onRemove, onShowDetails],
    )

    return (
        <Box className={classes.table}>
            <ApiQueryTable
                columns={columns}
                layoutMode="grid"
                displayColumnDefOptions={{
                    "mrt-row-actions": {
                        ...defaultDisplayColumnDefOptions["mrt-row-actions"],
                        size: COLUMN_SIZE.XXS,
                        maxSize: COLUMN_SIZE.XXS,
                        muiTableHeadCellProps: { className: classes.actionsCell },
                        muiTableBodyCellProps: { align: "center", className: classes.actionsCell },
                    },
                }}
                muiTablePaperProps={{ className: gridClasses.pinned }}
                initialState={{ density: "spacious" }}
                enableRowSelection
                getRowId={(member) => String(member.id)}
                state={{ rowSelection }}
                onRowSelectionChange={(updater) =>
                    onRowSelectionChange(typeof updater === "function" ? updater(rowSelection) : updater)
                }
                muiTableBodyRowProps={({ row }) => ({
                    className: cx(classes.row, !row.original.isActive && classes.inactiveRow),
                    onClick: (event) => {
                        if (isOrderRowControlClick(event)) return
                        onShowDetails(row.original)
                    },
                })}
                renderRowActionMenuItems={renderRowActionMenuItems}
                renderTopToolbarCustomActions={() => (
                    <Box className={classes.toolbar}>
                        <SelectDepartmentDropdown
                            departments={departments}
                            department={department}
                            setDepartment={onDepartmentChange}
                        />
                    </Box>
                )}
            />
        </Box>
    )
}
